/**
 * TilesetLibraryPanel — Visual tileset browser replacing the simple dropdown.
 *
 * Shows thumbnail cards for all library tilesets. Click to load into editor.
 * "+" button to import new tilesets. Right-click for remove/attribution.
 */

import { el } from '../utils/dom.js';

export class TilesetLibraryPanel {
  constructor(library, tilesetLoader, state) {
    this._library = library;
    this._loader = tilesetLoader;
    this._state = state;
    this._container = null;
    this._activeId = null;
    this._thumbnailCache = new Map(); // id → canvas dataURL

    this._library.onChange(() => this.render());
  }

  /**
   * Mount the library panel into a DOM container.
   * Replaces the old tileset-select dropdown with a card grid.
   */
  mount(container) {
    this._container = container;
    this.render();
  }

  render() {
    if (!this._container) return;
    this._container.innerHTML = '';

    const tilesets = this._library.list();

    // Header with title + add button
    const header = el('div', { className: 'tslib-header' },
      el('span', { className: 'tslib-title' }, 'Tilesets'),
      el('button', {
        className: 'tslib-add-btn',
        title: 'Import tileset from file',
        onClick: () => this._importTileset(),
      }, '+'),
    );
    this._container.appendChild(header);

    if (tilesets.length === 0) {
      this._container.appendChild(
        el('div', { className: 'tslib-empty' }, 'No tilesets yet. Click + to import.')
      );
      return;
    }

    // Card grid
    const grid = el('div', { className: 'tslib-grid' });

    for (const meta of tilesets) {
      const card = this._createCard(meta);
      grid.appendChild(card);
    }

    this._container.appendChild(grid);

    // Get More link
    const moreLink = el('div', { className: 'tslib-more' },
      el('a', {
        href: 'https://kenney.nl/assets?t=2d',
        target: '_blank',
        className: 'tslib-more-link',
      }, 'Get More Tilesets'),
    );
    this._container.appendChild(moreLink);
  }

  _createCard(meta) {
    const isActive = this._activeId === meta.id;
    const card = el('div', {
      className: `tslib-card${isActive ? ' tslib-card-active' : ''}`,
      title: `${meta.name}${meta.artist ? ` by ${meta.artist}` : ''}`,
      onClick: () => this._loadTileset(meta.id),
    });

    // Thumbnail
    const thumb = el('div', { className: 'tslib-thumb' });
    this._loadThumbnail(meta, thumb);
    card.appendChild(thumb);

    // Label
    const label = el('div', { className: 'tslib-label' }, meta.name);
    card.appendChild(label);

    // Right-click context menu
    card.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this._showContextMenu(e, meta);
    });

    return card;
  }

  async _loadThumbnail(meta, container) {
    // Check cache
    if (this._thumbnailCache.has(meta.id)) {
      const img = el('img', { src: this._thumbnailCache.get(meta.id), className: 'tslib-thumb-img' });
      container.appendChild(img);
      return;
    }

    // Load from local IDB (avoids R2 404 noise for bundled tilesets)
    try {
      let storedImg = await this._library._storage._loadImageFromIDB(`tilesets/${meta.id}/image.png`);
      if (!storedImg) storedImg = await this._library._storage.loadImage(`tilesets/${meta.id}/image.png`);
      // Create thumbnail (48x48 max, preserving aspect ratio)
      const thumbCanvas = document.createElement('canvas');
      const maxDim = 48;
      const scale = Math.min(maxDim / storedImg.width, maxDim / storedImg.height, 1);
      thumbCanvas.width = Math.max(1, Math.round(storedImg.width * scale));
      thumbCanvas.height = Math.max(1, Math.round(storedImg.height * scale));
      const ctx = thumbCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(storedImg, 0, 0, thumbCanvas.width, thumbCanvas.height);
      const dataUrl = thumbCanvas.toDataURL();
      this._thumbnailCache.set(meta.id, dataUrl);
      const img = el('img', { src: dataUrl, className: 'tslib-thumb-img' });
      container.appendChild(img);
    } catch {
      container.appendChild(el('span', { className: 'tslib-thumb-missing' }, '?'));
    }
  }

  async _loadTileset(id) {
    this._activeId = id;
    const tsData = await this._library.loadIntoEditor(id);
    if (tsData) {
      // Find the index of this tileset in editor state and sync the dropdown
      const tilesets = this._state.get('tilesets');
      const idx = tilesets.findIndex(ts => ts.image === tsData.image);
      const selectEl = document.getElementById('tileset-select');
      if (selectEl && idx >= 0) {
        selectEl.value = String(idx);
        selectEl.dispatchEvent(new Event('change'));
      }
    }
    this.render();
  }

  async _importTileset() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.webp';
    input.addEventListener('change', async () => {
      const file = input.files[0];
      if (!file) return;
      const meta = await this._library.addFromFile(file);
      if (meta) {
        await this._loadTileset(meta.id);
      }
    });
    input.click();
  }

  _showContextMenu(e, meta) {
    // Remove any existing context menu
    document.querySelectorAll('.tslib-ctx-menu').forEach(m => m.remove());

    const menu = el('div', {
      className: 'menu-dropdown tslib-ctx-menu',
      style: {
        position: 'fixed',
        left: `${e.clientX}px`,
        top: `${e.clientY}px`,
        zIndex: '10000',
      },
    });

    const items = [
      { label: `Load "${meta.name}"`, action: () => this._loadTileset(meta.id) },
    ];

    if (meta.artist) {
      items.push({ label: `Artist: ${meta.artist}`, disabled: true });
    }
    if (meta.license) {
      items.push({ label: `License: ${meta.license}`, disabled: true });
    }

    items.push({ separator: true });
    items.push({
      label: 'Remove from Library',
      action: async () => {
        await this._library.remove(meta.id);
        if (this._activeId === meta.id) this._activeId = null;
      },
    });

    for (const item of items) {
      if (item.separator) {
        menu.appendChild(el('div', { className: 'menu-separator' }));
        continue;
      }
      const menuItem = el('div', {
        className: `menu-dropdown-item${item.disabled ? ' disabled' : ''}`,
      });
      menuItem.appendChild(el('span', {}, item.label));
      if (!item.disabled) {
        menuItem.addEventListener('click', (ev) => {
          ev.stopPropagation();
          menu.remove();
          if (item.action) item.action();
        });
      }
      menu.appendChild(menuItem);
    }

    document.body.appendChild(menu);
    const closeMenu = () => {
      menu.remove();
      document.removeEventListener('click', closeMenu);
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
  }
}
